import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Alert, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import {
	organizerListReservationSeriesApi,
	organizerCancelReservationSeriesApi,
} from "../../../api/api";
import CourtSeriesKeptReport from "./CourtSeriesKeptReport";
import { intervalHeading } from "../../turnos/agenda/turnoFijoModel";
import {
	seriesCancelledMessage,
	seriesDateLabel,
	shortSeriesDate,
	weekdayName,
} from "./courtSeriesModel";
import "./NewInternalReservationModal.css";
import "./CourtSeries.css";

const DATE_FORMAT = "YYYY-MM-DD";

const upcomingLabel = (series) => {
	const count = Number(series.futureCount) || 0;
	if (count === 0 || !series.nextDate) return "Sin fechas próximas";
	return `${count} ${count === 1 ? "fecha próxima" : "fechas próximas"} · Próxima: ${seriesDateLabel(series.nextDate)}`;
};

/*
 * "Turnos fijos" (club grid toolbar): the club's active weekly series, each
 * with a "Cancelar serie" action that cancels every remaining date from today
 * (POST /api/organizer/reservations/series/:id/cancel). Dates already charged
 * or checked in are kept by the backend and listed here, because they still
 * stand. `onCancelled` lets the screen refresh the grid and toast; the report
 * of kept dates stays in this modal.
 */
export default function CourtSeriesListModal({
	show,
	bar,
	onHide,
	onCancelled,
}) {
	const clubId = bar?._id || null;
	const [status, setStatus] = useState("loading"); // loading | error | ready
	const [loadError, setLoadError] = useState("");
	const [items, setItems] = useState([]);
	const [confirmingId, setConfirmingId] = useState(null);
	const [cancellingId, setCancellingId] = useState(null);
	const [cancelError, setCancelError] = useState("");
	const [report, setReport] = useState(null);
	const requestRef = useRef(0);

	const load = useCallback(
		async ({ silent = false } = {}) => {
			if (!clubId) return;
			const requestId = ++requestRef.current;
			if (!silent) setStatus("loading");
			setLoadError("");
			try {
				const response = await organizerListReservationSeriesApi(clubId);
				if (requestId !== requestRef.current) return;
				const info = response?.data?.info;
				setItems(Array.isArray(info) ? info : []);
				setStatus("ready");
			} catch (err) {
				if (requestId !== requestRef.current) return;
				setLoadError(
					err?.response?.data?.message ||
						"No pudimos cargar los turnos fijos.",
				);
				setStatus("error");
			}
		},
		[clubId],
	);

	useEffect(() => {
		if (!show) return;
		setItems([]);
		setConfirmingId(null);
		setCancelError("");
		setReport(null);
		load();
	}, [show, load]);

	const handleHide = () => {
		if (cancellingId) return;
		requestRef.current += 1; // drop a response still in flight
		onHide?.();
	};

	const handleCancel = async (series) => {
		setCancellingId(series.id);
		setCancelError("");
		try {
			const response = await organizerCancelReservationSeriesApi(
				series.id,
				dayjs().format(DATE_FORMAT),
			);
			const result = response?.data?.info || {};
			setConfirmingId(null);
			setReport({ name: series.customer?.name, result });
			await onCancelled?.(result);
			await load({ silent: true });
		} catch (err) {
			setCancelError(
				err?.response?.data?.message ||
					"No pudimos cancelar el turno fijo. Intentá de nuevo.",
			);
		} finally {
			setCancellingId(null);
		}
	};

	return (
		<Modal
			show={show}
			onHide={handleHide}
			centered
			className="new-internal-reservation-modal court-series-modal"
		>
			<Modal.Header closeButton>
				<Modal.Title>
					Turnos fijos{bar?.name ? ` | ${bar.name}` : ""}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{cancelError && (
					<Alert variant="danger" role="alert">
						{cancelError}
					</Alert>
				)}
				{report && (
					<Alert variant="success" role="status">
						<div>
							{report.name ? `${report.name}: ` : ""}
							{seriesCancelledMessage(report.result)}
						</div>
						<CourtSeriesKeptReport
							kept={report.result.kept}
							intervalWeeks={report.result.series?.intervalWeeks}
						/>
					</Alert>
				)}

				{status === "loading" ? (
					<div className="csr-state" role="status">
						<Spinner animation="border" size="sm" /> Cargando turnos fijos...
					</div>
				) : status === "error" ? (
					<div role="alert">
						<p className="csr-state">{loadError}</p>
						<button
							type="button"
							className="zr-btn zr-btn--soft"
							onClick={() => load()}
						>
							Reintentar
						</button>
					</div>
				) : items.length === 0 ? (
					<p className="csr-state">
						No hay turnos fijos activos. Podés crear uno desde una reserva nueva
						con la opción "Turno fijo".
					</p>
				) : (
					<ul className="csr-series" aria-label="Turnos fijos activos">
						{items.map((series) => (
							<li key={series.id} className="csr-series__item">
								<span className="csr-series__title">
									{series.customer?.name || "Sin nombre"}
								</span>
								<span className="csr-series__meta">
									{series.courtName || "Cancha"} · {intervalHeading(series.intervalWeeks)} ·{" "}
									{weekdayName(series.weekday)} {series.slotStart}
									{series.durationMinutes ? ` · ${series.durationMinutes} min` : ""}
								</span>
								<span className="csr-series__meta">
									Desde {shortSeriesDate(series.dateFrom)} hasta{" "}
									{shortSeriesDate(series.dateTo)}
								</span>
								<span className="csr-series__meta">{upcomingLabel(series)}</span>
								{confirmingId === series.id ? (
									<div className="csr-series__actions">
										<span className="csr-series__meta">
											¿Cancelar todas las fechas que quedan desde hoy?
										</span>
										<button
											type="button"
											className="zr-btn zr-btn--ghost"
											disabled={cancellingId === series.id}
											onClick={() => setConfirmingId(null)}
										>
											Volver
										</button>
										<button
											type="button"
											className="zr-btn zr-btn--dark"
											disabled={cancellingId === series.id}
											onClick={() => handleCancel(series)}
										>
											{cancellingId === series.id ? (
												<Spinner animation="border" size="sm" />
											) : (
												"Sí, cancelar serie"
											)}
										</button>
									</div>
								) : (
									<div className="csr-series__actions">
										<button
											type="button"
											className="zr-btn zr-btn--soft"
											disabled={Boolean(cancellingId)}
											onClick={() => {
												setCancelError("");
												setConfirmingId(series.id);
											}}
										>
											Cancelar serie
										</button>
									</div>
								)}
							</li>
						))}
					</ul>
				)}
			</Modal.Body>
		</Modal>
	);
}
